import React, { useState, useRef, useEffect } from 'react';
import { Upload, RotateCcw, Trash, ImagePlus, BadgeInfo, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import './PetCustomizer.scss';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { useSelector, useDispatch } from 'react-redux';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import useFileHandlers from '../../hooks/useFileHandlers';
import { useAffiliate } from '../../hooks/useAffiliate';
import Button from '../ui/Button';
import FramesModal from './FramesModal';
import { Tooltip } from 'react-tooltip';
import { toast } from 'react-toastify';
import {
  drawCanvas
} from './functions/canvasRenderer';
import {
  loadImagesFromDB
} from './functions/loadImagesFromIndexedDB';
import {
  handlePointerDown,
  handlePointerMove,
  handlePointerUp
} from './functions/pointerHandlers';
import {
  downloadCanvas
} from './functions/downloadCanvas';
import {
  saveCanvas
} from './functions/saveCanvas';
import {
  saveCanvasAPI
} from './functions/saveCanvasAPI';
import { CANVAS_HEIGHT, CANVAS_WIDTH, CUSTOMIZER_MAX_IMAGES_COUNT } from '../../config/config';
import upload from '/icons/upload.svg';
import dog_icon from '/icons/dog-icon.svg';
import cat_icon from '/icons/cat-icon.svg';
import {
  duplicateImageAsync,
  setSelectedFrame,
  setSelectedFrameCategory,
  setIncludePetName,
  setPetName,
  setPetType,
  selectUploadedImageIds,
  removeImage,
  moveImage,
  rotateImage,
  scaleImage,
  resetCustomizer,
} from '../../store/customizerSlice';
import { deleteImageFromDB, clearStore } from '../../utils/indexedDB';
import { EDGE_HOST } from "../../config/endpoints";


const PetCustomizer = ({ 
  subscriptionPage = false, 
  setSubscriptionPageStep, 
  onSubmit,
  apiMode = false,
  sessionUuid = null,
  supabaseUrl = EDGE_HOST,
  allowedPetTypes = ['Dog', 'Cat']
}) => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { affiliate } = useAffiliate();
  const { handleFileSelect, handleDrop } = useFileHandlers();

  const canvasWrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const frameSelectionRef = useRef(null);
  const innerFrameContentRef = useRef(null);
  const framesSwiperRef = useRef(null);
  const imageCache = useRef(new Map());
  const petSelection = useRef(null);
  const touchesRef = useRef({
    initialDistance: null,
    initialScale: null,
  });

  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [staticBg, setStaticBg] = useState(null);
  const [frameImage, setFrameImage] = useState(null);
  const [imageElements, setImageElements] = useState(new Map());
  const [frameSectionHeight, setFrameSectionHeight] = useState(null);
  const [isFramesModalActive, setFramesModalActive] = useState(false);
  const [isPetSelectionExpanded, setIsPetSelectionExpanded] = useState(true);
  const [isFrameSelectionExpanded, setIsFrameSelectionExpanded] = useState(true);
  const [notSelectedKey, setNotSelectedKey] = useState(0);
  const [runOnRender, setRunOnRender] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const includePetName = useSelector((state) => state.customizer.includePetName);
  const petName = useSelector((state) => state.customizer.petName);
  const petType = useSelector((state) => state.customizer.petType);
  const images = useSelector((state) => state.customizer.images);
  const frames = useSelector((state) => state.customizer.frames);
  const framesCategories = useSelector((state) => state.customizer.framesCategories);
  const selectedFrame = useSelector((state) => state.customizer.selectedFrame);
  const selectedFrameCategory = useSelector((state) => state.customizer.selectedFrameCategory);
  const uploadedImageIds = useSelector(selectUploadedImageIds);
  const cartItems = useSelector((state) => state.cart.cartItems);

  const filteredFrames = frames.filter((f) => f.category === selectedFrameCategory);

  // ─── API Mode: Reset all state when a new session UUID is received ───────────
  // This fires every time the SDK opens a fresh modal session, preventing stale
  // images, pet name, and pet type from a previous personalization from persisting.
  useEffect(() => {
    if (!apiMode || !sessionUuid) return;

    // Clear IndexedDB uploaded images so old blobs don't reload
    clearStore('uploadedImages').catch((err) =>
      console.error('[TreatInk] Failed to clear IndexedDB on session reset:', err)
    );

    // Reset Redux customizer state (images, petName, petType, frame back to default)
    dispatch(resetCustomizer());

    // Clear the local React image cache and selection
    imageCache.current = new Map();
    setImageElements(new Map());
    setSelectedImageIndex(null);

    // Auto-select pet type when only one is allowed (e.g. dog-only products)
    if (allowedPetTypes.length === 1) {
      dispatch(setPetType(allowedPetTypes[0]));
    }
  }, [sessionUuid]); // Re-runs on every new session UUID — i.e. every modal open
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (framesSwiperRef.current) {
      framesSwiperRef.current.slideTo(0, 0);
    }
  }, [selectedFrameCategory]);

  useEffect(() => {
    if (!runOnRender) {
      setRunOnRender(true);
      return;
    }
    if (!frameSelectionRef.current || !innerFrameContentRef.current) return;
    requestAnimationFrame(() => {
      const heightToSet = isFrameSelectionExpanded
        ? `${innerFrameContentRef.current.offsetHeight + 10}px`
        : "0px";
      setFrameSectionHeight(heightToSet);
    });
  }, [filteredFrames.length, selectedFrameCategory, isFrameSelectionExpanded]);

  useEffect(() => {
    if (images.length > 0) {
      const lastImage = images[images.length - 1];
      if (selectedImageIndex === null || images[selectedImageIndex]?.id !== lastImage.id) {
        setSelectedImageIndex(images.length - 1);
      }
    }
  }, [images.length]);

  useEffect(() => {
    if (staticBg) {
      setSelectedImageIndex((prev) => prev);
    }
  }, [staticBg]);

  useEffect(() => {
    const img = new Image();
    img.src = "/images/background.png";
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setStaticBg(img);
    };
  }, []);

  useEffect(() => {
    document.fonts.ready.then(() => {
      setTimeout(() => {
        setPetName((prev) => prev + " ");
        setTimeout(() => setPetName((prev) => prev.trim()), 10);
      }, 50);
    });
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = selectedFrame.url;
    img.onload = () => setFrameImage(img);
  }, [selectedFrame]);

  useEffect(() => {
    drawCanvas({
      canvas: canvasRef.current,
      images,
      staticBg,
      frameImage,
      imageCache,
      selectedFrame,
      includePetName,
      petName,
      isDragging,
    });
  }, [images, frameImage, staticBg, imageElements, selectedFrame, petName, includePetName, isDragging]);

  useEffect(() => {
    const load = async () => {
      if (images.length === 0) return;
      const newImages = await loadImagesFromDB(images, imageCache);
      if (newImages.size > 0) {
        setImageElements((prev) => new Map([...prev, ...newImages]));
      }
    };
    load();
  }, [images.length]);

  useEffect(() => {
    const disableScroll = (e) => {
      if (e.target.closest('.customizer-outer-container')) {
        e.preventDefault();
      }
    };

    if (isDragging) {
      document.body.style.overflow = "hidden";
      document.addEventListener("touchmove", disableScroll, { passive: false });
    } else {
      document.body.style.overflow = "";
      document.removeEventListener("touchmove", disableScroll);
    }

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("touchmove", disableScroll);
    };
  }, [isDragging]);


  const pointerDownHandler = (e) => handlePointerDown(e, canvasRef, images, setSelectedImageIndex, setDragStart, setIsDragging, CANVAS_WIDTH, CANVAS_HEIGHT, touchesRef);
  const pointerMoveHandler = (e) => handlePointerMove(e, isDragging, selectedImageIndex, canvasRef, images, dragStart, dispatch, moveImage, CANVAS_WIDTH, CANVAS_HEIGHT, touchesRef);
  const pointerUpHandler = () => handlePointerUp(setDragStart, setIsDragging, touchesRef);

  const rotateImageRight = () => {
    if (selectedImageIndex !== null) {
      dispatch(rotateImage({ id: images[selectedImageIndex].id, angle: 15 }));
    }
  };

  const rotateImageLeft = () => {
    if (selectedImageIndex !== null) {
      dispatch(rotateImage({ id: images[selectedImageIndex].id, angle: -15 }));
    }
  };

  const deleteImageHandler = () => {
    if (selectedImageIndex !== null) {
      dispatch(removeImage(images[selectedImageIndex].id));
      deleteImageFromDB("uploadedImages", images[selectedImageIndex].id);
      setSelectedImageIndex(null);
    }
  };

  const scaleImageHandler = (newScale) => {
    if (selectedImageIndex !== null) {
      dispatch(scaleImage({ id: images[selectedImageIndex].id, scale: newScale }));
    }
  };

  const saveAndRedirect = async () => {
    // API Mode: Save to API and post message back to SDK
    if (apiMode && sessionUuid) {
      setIsSaving(true);
      try {
        const result = await saveCanvasAPI({
          sessionUuid,
          supabaseUrl,
          petType,
          petName,
          selectedFrame,
          canvasRef,
          image: images[selectedImageIndex],
          uploadedImageIds,
          toast,
          setNotSelectedKey,
        });
        
        // Send success message to parent window (SDK)
        // The SDK will show a loading spinner in the modal while it adds to cart
        if (window.parent !== window) {
          window.parent.postMessage({
            type: 'treatink_artwork_saved',
            sessionUuid: sessionUuid,
            success: true,
            artworkUrl: result.urls?.canvasImageUrl || null
          }, '*');
        }
        // Note: do NOT setIsSaving(false) here — the SDK takes over from this point
        // and navigates away. Keeping isSaving true prevents any further interaction.
        return;
      } catch (error) {
        console.error('API save failed:', error);
        setIsSaving(false);
        return;
      }
    }
    
    // Normal mode: existing logic
    saveCanvas({
      petType,
      setNotSelectedKey,
      toast,
      cartItems,
      canvasRef,
      uploadedImageIds,
      petName,
      selectedFrame,
      navigate,
      setStaticBg,
      image: images[selectedImageIndex],
      redirectTo: !subscriptionPage ? '/product-select' : null
    })
    if (subscriptionPage && setSubscriptionPageStep && petType) {
      setSubscriptionPageStep(4);
      if (onSubmit) {
        onSubmit();
      }
    }
  };

  const downloadCurrentCanvas = () => downloadCanvas(canvasRef, setSelectedImageIndex);


  return (
    <>
      <div className={`full-width customizer-outer-container flex justify-center ${(affiliate && !subscriptionPage) ? 'affiliate-margin' : ''} ${subscriptionPage ? 'subscriptionPage-styles' : ''} ${apiMode ? 'api-mode-customizer' : ''}`}>
        <div className={`bg-white rounded-20 contained-width customizer-container overflow-hidden ${subscriptionPage ? '' : 'box-shadow'}`}>
          <div className={`customizer-container-inner gap-5 md:gap-10 ${subscriptionPage ? 'p-0' : 'p-10'}`}>

            {(affiliate && !subscriptionPage) && (
              <img
                src={affiliate.logo}
                alt={`${affiliate.name} logo`}
                className="affiliate-logo-customizer"
              />
            )}

            {/* Controls container */}
            <div className="customizer-controls-container gap-2 md:gap-5">

              {images.length > 0 && (
                <div className="flex items-center gap-2 customizer-controls customizer-image-controls" data-ignore-image-deselect>
                  <div className='flex flex-col gap-2'>
                    <label className="block  font-medium text-gray-700">
                      Image Controls
                    </label>

                    <div>
                      <button
                        onClick={rotateImageLeft}
                        className="p-3 hover:bg-gray-100 rounded"
                        data-tooltip-id="rotate-left"
                      >
                        <RotateCcw size={20} />
                      </button>
                      <Tooltip className="controls-tooltip" opacity="1" id="rotate-left" content="Rotate Left" place="top" />

                      <button
                        onClick={rotateImageRight}
                        className="p-3 hover:bg-gray-100 rounded"
                        data-tooltip-id="rotate-right"
                      >
                        <RotateCcw size={20} className="transform scale-x-[-1]" />
                      </button>
                      <Tooltip className="controls-tooltip" opacity="1" id="rotate-right" content="Rotate Right" place="top" />

                      <button
                        onClick={deleteImageHandler}
                        className="p-3 hover:bg-gray-100 rounded"
                        data-tooltip-id="delete"
                      >
                        <Trash size={20} />
                      </button>
                      <Tooltip className="controls-tooltip" opacity="1" id="delete" content="Delete Image" place="top" />
                    </div>
                  </div>
                  {selectedImageIndex !== null && images[selectedImageIndex] && (
                    <div className="mt-4 slider-input">
                      <div
                        className="tooltip"
                        style={{
                          left: `${(
                            (images[selectedImageIndex].scale - 0.4) /
                            (images[selectedImageIndex].maxScale - 0.4)
                          ) * 100}%`,
                        }}
                      >
                        {`${Math.round(images[selectedImageIndex].width * images[selectedImageIndex].scale)} x ${Math.round(
                          images[selectedImageIndex].height * images[selectedImageIndex].scale
                        )}px`}
                      </div>
                      <input
                        type="range"
                        min={0.5}
                        max={images[selectedImageIndex].maxScale}
                        step="0.1"
                        value={images[selectedImageIndex].scale}
                        onChange={(e) =>
                          scaleImageHandler(parseFloat(e.target.value))
                        }
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Pet name */}
              <div className="customizer-controls pet-name-container">
                <div className='flex flex-col gap-2 w-full items-center' data-ignore-image-deselect>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={includePetName}
                      onChange={() => dispatch(setIncludePetName(!includePetName))}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="font-medium text-gray-700 cursor-pointer">Include Pet Name on Label</span>
                  </label>

                  {includePetName && (
                    <div className="flex flex-col gap-2 w-full items-center">
                      <input
                        value={petName}
                        type="text"
                        maxLength="20"
                        placeholder="Pet Name"
                        onChange={(e) => dispatch(setPetName(e.target.value))}
                        className="pet-name-input"
                      />
                    </div>
                  )}
                </div>
              </div>


              {/* Pet Type Selection - hidden when only one type is allowed (auto-selected) */}
              {allowedPetTypes.length > 1 && (
                <div
                  key={!petType ? `pet-type-${notSelectedKey}` : 'pet-type'}
                  className={`customizer-controls ${(!petType && notSelectedKey !== 0) ? 'not-selected' : ''}`}
                >
                  <div
                    className="flex items-center justify-center gap-2 w-full cursor-pointer"
                    onClick={() => setIsPetSelectionExpanded(!isPetSelectionExpanded)}
                    data-ignore-image-deselect
                  >
                    <label className="block font-medium text-gray-700">Choose Your Pet</label>
                    {isPetSelectionExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>

                  <div
                    ref={petSelection}
                    className="collapsible-content"
                    style={{
                      height: isPetSelectionExpanded ? `${petSelection.current?.scrollHeight}px` : "0px",
                    }}
                    data-ignore-image-deselect
                  >
                    <div className="flex gap-4 mt-4 justify-center">
                      {allowedPetTypes.includes('Dog') && (
                        <button
                          onClick={() => dispatch(setPetType("Dog"))}
                          className={`pet-selection-button rounded-10 border-2 gap-2 w-24 p-2 flex flex-col items-center justify-center
                          ${petType === "Dog" ? "selected" : ""}`}
                        >
                          <img src={dog_icon} alt="" />
                          <span className="">Dog</span>
                        </button>
                      )}

                      {allowedPetTypes.includes('Cat') && (
                        <button
                          onClick={() => dispatch(setPetType("Cat"))}
                          className={`pet-selection-button rounded-10 border-2 gap-2 w-24 p-2 flex flex-col items-center justify-center
                          ${petType === "Cat" ? "selected" : ""}`}
                        >
                          <img src={cat_icon} alt="" />
                          <span className="">Cat</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}


              {/* Frame select */}
              <div className="customizer-controls">
                <div
                  className="flex items-center justify-center gap-2 w-full cursor-pointer"
                  onClick={() => setIsFrameSelectionExpanded(!isFrameSelectionExpanded)}
                  data-ignore-image-deselect
                >
                  <label className="block font-medium text-gray-700">Choose Your Background</label>
                  {isFrameSelectionExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>

                <div
                  ref={frameSelectionRef}
                  className="collapsible-content"
                  style={{
                    height:
                      frameSectionHeight !== null
                        ? frameSectionHeight
                        : isFrameSelectionExpanded
                          ? `${frameSelectionRef.current?.scrollHeight || 0}px`
                          : "0px"
                  }}
                  data-ignore-image-deselect
                >
                  <div ref={innerFrameContentRef}>
                    {/* Category Filters */}
                    <div className="flex gap-2 justify-center flex-wrap mt-2">
                      {framesCategories.map((category) => (
                        <button
                          key={category}
                          onClick={() => dispatch(setSelectedFrameCategory(category))}
                          className={`frame-category px-2 py-1 rounded-10 transition ${selectedFrameCategory === category ? "selected" : ""
                            }`}
                        >
                          {category}
                        </button>
                      ))}
                    </div>

                    <div className="flex flex-col items-center justify-center gap-4 mt-3">
                      <Swiper
                        modules={[Pagination]}
                        onSwiper={(swiper) => {
                          framesSwiperRef.current = swiper;
                        }}
                        pagination={{ el: ".frames-pagination", clickable: true }}
                        spaceBetween={10}
                        slidesPerView="auto"
                        slidesPerGroup={3}
                      >
                        {filteredFrames.map((bg, index) => (
                          <SwiperSlide key={index}>
                            <button
                              onClick={() => dispatch(setSelectedFrame(bg))}
                              className={`relative bg-buttons w-full rounded-10 overflow-hidden border-2 ${selectedFrame.url === bg.url ? "selected" : ""}`}
                            >
                              <img src={bg.url} alt={`Frame ${index + 1}`} className="relative w-full h-full object-cover z-10" />

                              {/* Uploaded image */}
                              {images[selectedImageIndex] && imageCache.current.has(images[selectedImageIndex].id) && (
                                <img
                                  src={imageCache.current.get(images[selectedImageIndex].id).src}
                                  alt="Uploaded Image"
                                  className="absolute z-0 inset-0 w-full h-full object-cover opacity-100"
                                />
                              )}
                            </button>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                      <div className="frames-pagination" />
                    </div>

                    <div className='flex justify-center mt-3'>
                      <button onClick={() => setFramesModalActive(true)} className="frames-modal-btn">
                        Browse All
                      </button>
                    </div>

                    <FramesModal
                      isOpen={isFramesModalActive}
                      onClose={() => setFramesModalActive(false)}
                      selectedImage={images[selectedImageIndex]}
                      imageCache={imageCache}
                    />

                  </div>
                </div>
              </div>

              {!subscriptionPage &&
                <div className="flex gap-3 justify-between items-center" data-ignore-image-deselect>
                  <Tooltip className="controls-tooltip" opacity="1" id="download-result" content="Download Result" place="top" />
                  <Button
                    onClick={saveAndRedirect}
                    type="button"
                    variant="filled"
                    className="justify-between"
                    disabled={images.length === 0 || isSaving}
                  >
                    {isSaving ? 'Saving...' : (apiMode ? 'Save Customization' : 'Next Step')}<ArrowRight size={30} />
                  </Button>
                </div>
              }

            </div>

            {/* Canvas area */}
            <div className="customizer-content">
              <div
                ref={canvasWrapperRef}
                className="relative border-3 border-dashed rounded-20 flex items-center justify-center overflow-hidden"
                style={{
                  width: '100%',
                  aspectRatio: '3 / 4',
                  borderColor: 'var(--purple)',
                  cursor: isDragging ? 'grabbing' : 'pointer'
                }}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onMouseDown={pointerDownHandler}
                onMouseMove={pointerMoveHandler}
                onMouseUp={pointerUpHandler}
                onTouchStart={pointerDownHandler}
                onTouchMove={pointerMoveHandler}
                onTouchEnd={pointerUpHandler}
              >
                <canvas
                  ref={canvasRef}
                  style={{ width: '100%', height: '100%' }}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                />
                <canvas
                  ref={overlayRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    zIndex: 4,
                    pointerEvents: 'none',
                  }}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                />

                {!images.length && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center upload-container">
                    <img src={upload} alt="" />
                    <p className="text-black">
                      Drag your pet's photo here<br />
                      and start personalizing!
                    </p>

                    <Button
                      onClick={() => document.getElementById('fileInput').click()}
                      type="button"
                      variant="filled"
                      color="secondary"
                      className="fit-width"
                    >
                      Or Select Image
                    </Button>
                  </div>
                )}

              </div>
            </div>
          </div>
          <input
            id="fileInput"
            type="file"
            accept="image/png, image/jpeg, image/webp"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      </div>

      {subscriptionPage &&
        <Button
          onClick={saveAndRedirect}
          type="button"
          variant="filled"
          className="gap-4 justify-between"
          disabled={images.length === 0 || isSaving}
          style={{ maxWidth: 'fit-content' }}
        >
          {isSaving ? 'Saving...' : 'Next Step'}<ArrowRight size={20} />
        </Button>
      }
    </>
  );
};

export default PetCustomizer;
